import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useGame, BUILDING_COIN_INCOME, BUILDING_COSTS, BuildingType } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";

const BUILDING_ICONS: Record<BuildingType, string> = {
  house: "ri-home-4-line",
  company: "ri-building-4-line",
  powerPlant: "ri-flashlight-line",
};

const BUILDING_COLORS: Record<BuildingType, { text: string; bg: string; border: string; badge: string }> = {
  house: { text: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20", badge: "bg-emerald-400/15 text-emerald-400" },
  company: { text: "text-sky-400", bg: "bg-sky-400/10", border: "border-sky-400/20", badge: "bg-sky-400/15 text-sky-400" },
  powerPlant: { text: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/20", badge: "bg-yellow-400/15 text-yellow-400" },
};

export default function BuildingsSummary() {
  const { user } = useAuth();
  const { blocks, companies, powerPlants, houses } = useGame();
  const { isDark } = useTheme();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const myBlocks = user ? Object.values(blocks).filter((b) => b.ownerId === user.id) : [];

  const buildingCounts: Record<BuildingType, number> = { house: 0, company: 0, powerPlant: 0 };

  const BUILDING_LABELS: Record<BuildingType, string> = {
    house: t("buildingHouseLabel"),
    company: t("buildingCompanyLabel"),
    powerPlant: t("buildingPowerPlantLabel"),
  };

  const BUILDING_DESCS: Record<BuildingType, string> = {
    house: t("buildingHouseDesc"),
    company: t("buildingCompanyDesc"),
    powerPlant: t("buildingPowerPlantDesc"),
  };

  const buildingDetails: Array<{
    type: BuildingType;
    blockId: string;
    buildingId: string;
    lat: number;
    lng: number;
    builtAt: string;
    name?: string;
  }> = [];

  myBlocks.forEach((b) => {
    b.buildings.forEach((bld) => {
      buildingCounts[bld.type]++;
      let name: string | undefined;
      if (bld.type === "company" && companies[bld.buildingId]) {
        name = companies[bld.buildingId].name;
      } else if (bld.type === "powerPlant" && powerPlants[bld.buildingId]) {
        name = powerPlants[bld.buildingId].name;
      } else if (bld.type === "house" && houses[bld.buildingId]) {
        name = `${t("buildingHouseLabel")} (${houses[bld.buildingId].residents.length}/${houses[bld.buildingId].maxResidents})`;
      }
      buildingDetails.push({
        type: bld.type,
        blockId: b.blockId,
        buildingId: bld.buildingId,
        lat: b.lat,
        lng: b.lng,
        builtAt: bld.builtAt,
        name,
      });
    });
  });

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";

  const handleBuildingClick = (type: BuildingType, buildingId: string) => {
    if (type === "company") navigate(`/company/${buildingId}`);
    else if (type === "powerPlant") navigate(`/powerplant/${buildingId}`);
    else if (type === "house") navigate(`/house/${buildingId}`);
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className={`text-lg font-bold ${textPrimary}`}>{t("buildingMgmt")}</h2>
          <p className={`text-sm ${textSecondary}`}>{t("buildingTotal")} {buildingDetails.length} {t("buildingUnit")}</p>
        </div>
      </div>

      {/* Building Type Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {(Object.keys(BUILDING_LABELS) as BuildingType[]).map((type) => {
          const c = BUILDING_COLORS[type];
          const count = buildingCounts[type];
          const totalIncome = count * BUILDING_COIN_INCOME[type];
          const totalCost = count * BUILDING_COSTS[type];
          return (
            <div key={type} className={`rounded-2xl border p-5 ${cardBg} ${c.border}`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${c.bg}`}>
                  <i className={`${BUILDING_ICONS[type]} text-xl ${c.text}`}></i>
                </div>
                <span className={`text-xs rounded-full px-2.5 py-1 font-semibold ${c.badge}`}>{count} {t("buildingUnit")}</span>
              </div>
              <h3 className={`text-base font-bold mb-1 ${textPrimary}`}>{BUILDING_LABELS[type]}</h3>
              <p className={`text-xs mb-4 ${textSecondary}`}>{BUILDING_DESCS[type]}</p>
              <div className={`space-y-2 pt-3 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>{t("buildCostPerUnit")}</span>
                  <span className={`font-semibold ${textPrimary}`}>{BUILDING_COSTS[type].toLocaleString()} coins</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>{t("incomePerHourPerUnit")}</span>
                  <span className={`font-semibold ${c.text}`}>+{BUILDING_COIN_INCOME[type]} coins</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>{t("totalIncomePerHour")}</span>
                  <span className={`font-bold ${c.text}`}>+{totalIncome} coins</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>{t("totalInvestment")}</span>
                  <span className={`font-semibold ${textPrimary}`}>{totalCost.toLocaleString()} coins</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Building List */}
      {buildingDetails.length > 0 && (
        <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
          <div className={`px-5 py-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
            <h3 className={`text-sm font-bold ${textPrimary}`}>{t("buildingListTitle")}</h3>
          </div>
          <div className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
            {buildingDetails
              .sort((a, b) => new Date(b.builtAt).getTime() - new Date(a.builtAt).getTime())
              .map((bld, i) => {
                const c = BUILDING_COLORS[bld.type];
                const date = new Date(bld.builtAt);
                return (
                  <button
                    key={i}
                    onClick={() => handleBuildingClick(bld.type, bld.buildingId)}
                    className={`w-full flex items-center gap-4 px-5 py-3.5 transition-colors text-left cursor-pointer ${rowHover}`}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${c.bg}`}>
                      <i className={`${BUILDING_ICONS[bld.type]} text-sm ${c.text}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${textPrimary}`}>
                        {bld.name || BUILDING_LABELS[bld.type]}
                      </p>
                      <p className={`text-xs font-mono ${textSecondary}`}>
                        {bld.lat.toFixed(4)}, {bld.lng.toFixed(4)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 flex items-center gap-3">
                      <div>
                        <p className={`text-xs font-semibold ${c.text}`}>+{BUILDING_COIN_INCOME[bld.type]}/h</p>
                        <p className={`text-xs ${textSecondary}`}>
                          {date.toLocaleDateString(lang === "ja" ? "ja-JP" : "en-US", { month: "short", day: "numeric" })}
                        </p>
                      </div>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                        <i className={`ri-arrow-right-s-line text-sm ${textSecondary}`}></i>
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {buildingDetails.length === 0 && (
        <div className={`rounded-2xl border p-12 text-center ${cardBg}`}>
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-400/10 rounded-2xl">
            <i className="ri-building-2-line text-3xl text-amber-400"></i>
          </div>
          <p className={`font-semibold mb-1 ${textPrimary}`}>{t("noBuildingsTitle")}</p>
          <p className={`text-sm ${textSecondary}`}>{t("noBuildingsDesc")}</p>
        </div>
      )}
    </div>
  );
}
